import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemAirport } from '../../resources/types';


// --- PROPS INTERFACES ---

// When single = true
interface SelectAirportSingleProps {
    single: true; //just true
    value?: number; // Single ID
    width?: number;
    onSelect?: (selected: number | null) => void; // <--- CHANGED: Returns ID or null
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
    dataset?: ItemAirport[];
    disabledAirports?: number[];
    filteringType?: 'auto' | 'none';
}

// When single = false (or undefined, as per union type)
interface SelectAirportMultiProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    onSelect?: (selected: number[]) => void; // <--- CHANGED: Returns array of IDs
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
    dataset?: ItemAirport[];
    disabledAirports?: number[];
    filteringType?: 'auto' | 'none';
}

export type SelectAirportProps =
    | SelectAirportSingleProps
    | SelectAirportMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectAirport: React.FC<SelectAirportProps> = (props) => {
    const {
        single,
        value,
        width = 200,
        readonly,
        tokenLimit = 3,
        dataset = [],
        hideTokens = false,
        filteringType = "none",
        onSelect,
        disabledAirports = []
    } = props;
    const { data } = dataset.length > 0 ? { data: { airports: dataset } } : useGlobalState();
    if (!data || !data.airports) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemAirport | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemAirport[]>([]);
    // Memoize the calculation of available options

    const mappedAirports: ItemAirport[] = useMemo(() => {
        // 1. Map the raw data
        console.log(disabledAirports);
        const airports = data.airports
            .filter((plan: any) => !disabledAirports.includes(plan.idAirport))
            .map((plan: any) => {
                // Create the label with a hard fallback to an empty string
                const labelText = plan.airport || plan.city || plan.IATA || "";

                return {
                    ...plan,
                    iconUrl: `https://purecatamphetamine.github.io/country-flag-icons/3x2/${plan.country ? plan.country.toUpperCase() : ''}.svg`,
                    label: labelText, // Guaranteed to be at least ""
                    value: plan.idAirport?.toString() ?? "",
                    description: `${plan.IATA ?? ''} - ${plan.city || ''}`,
                };
            });

        // 2. Sort
        return airports.sort((a: any, b: any) => {
            const isASelected = single
                ? value === a.idAirport
                : (value as number[] | undefined)?.includes(a.idAirport);

            const isBSelected = single
                ? value === b.idAirport
                : (value as number[] | undefined)?.includes(b.idAirport);

            if (isASelected && !isBSelected) return -1;
            if (!isASelected && isBSelected) return 1;

            // Now safe because label is guaranteed to be a string
            return (a.label || "").localeCompare(b.label || "");
        });
    }, [data.airports, value, single]);


    // --- Initialization & Value Sync Effect ---

    useEffect(() => {
        if (!value) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE: Value is a number (ID)
            const id = value as number;
            const match = mappedAirports.find(airport => airport.idAirport === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedAirports.filter(airport => ids.includes(airport.idAirport));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedAirports]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemAirport | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idAirport ?? null; // Extract ID
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemAirport[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idAirport); // Extract array of IDs
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    const placeholder = single ? "Seleccionar aeropuerto" : "Seleccionar aeropuertos";
    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    filteringType={filteringType}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedAirports}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    filteringType={filteringType}
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={selectedMulti.length === 0 ? placeholder : `${selectedMulti.length}/${mappedAirports.length} seleccionadas`}
                    selectedOptions={selectedMulti}
                    options={mappedAirports}
                    onChange={handleMultiChange}
                />
            )}

        </div>
    );
};

export default SelectAirport;